import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const PRODUCTS = [
  {
    slug: "haldi-turmeric",
    name: "Haldi",
    subtitle: "Turmeric Powder",
    tagline: "High-curcumin Indian turmeric",
    description:
      "Premium-grade turmeric powder with 3-5% curcumin. Bright golden color, low moisture, sourced from Maharashtra & Andhra Pradesh.",
    image:
      "https://images.unsplash.com/photo-1615485500834-bc10199bc727?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    specs: ["Curcumin 3-5%", "Moisture < 8%", "Powder / Fingers"],
    tone: "amber",
  },
  {
    slug: "spices",
    name: "Spices",
    subtitle: "Whole & Ground",
    tagline: "Chilli · Cumin · Coriander · Pepper",
    description:
      "A full range of Indian spices — whole and ground — cleaned, graded and packed for food-processing and retail markets worldwide.",
    image:
      "https://images.unsplash.com/photo-1606951444141-e5533feb55be?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    specs: ["Whole & ground", "Custom blends", "Steam-sterilized"],
    tone: "rose",
  },
  {
    slug: "vegetables",
    name: "Vegetables",
    subtitle: "Fresh & Dehydrated",
    tagline: "Onion · Garlic · Potato",
    description:
      "Farm-direct fresh produce and high-quality dehydrated vegetables — flakes, granules and minced grades for the food industry.",
    image:
      "https://images.pexels.com/photos/5425795/pexels-photo-5425795.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1200",
    specs: ["Fresh & dehydrated", "Multiple grades", "Year-round supply"],
    tone: "green",
  },
  {
    slug: "onion-powder",
    name: "Onion Powder",
    subtitle: "Food-grade",
    tagline: "Free-flowing · Low-moisture",
    description:
      "Spray and roller dried onion powder — low moisture, free-flowing, used in seasonings, sauces, soups and ready-to-eat foods.",
    image:
      "https://images.unsplash.com/photo-1595414902678-862fe51c9f27?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    specs: ["Moisture < 6%", "Mesh 60–100", "White & toasted"],
    tone: "cream",
  },
  {
    slug: "coconut-shell-powder",
    name: "Coconut Shell",
    subtitle: "Powder",
    tagline: "Industrial grade · Multi-application",
    description:
      "Finely ground coconut shell powder used in incense, mosquito coils, plywood adhesives and abrasive industries.",
    image:
      "https://images.unsplash.com/photo-1553787434-dd9eb4ea4d0b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    specs: ["Mesh 80–200", "Moisture < 10%", "Bulk / Jumbo bags"],
    tone: "brown",
  },
  {
    slug: "chocolates",
    name: "Chocolates",
    subtitle: "Premium Confectionery",
    tagline: "Couverture · Compound · Filled",
    description:
      "Couverture and compound chocolates for retail and industrial buyers — smooth melt, balanced sweetness and a full private-label program.",
    image:
      "https://images.pexels.com/photos/918327/pexels-photo-918327.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1200",
    specs: ["Dark / Milk / White", "Cocoa 30–70%", "Private label"],
    tone: "cocoa",
  },
  {
    slug: "match-boxes",
    name: "Match Boxes",
    subtitle: "Sivakasi Safety Matches",
    tagline: "Kitchen & BBQ matches · OEM",
    description:
      "Premium Indian safety matches from Sivakasi — clean strike, long smoke-free burn, OEM and private-label printing available.",
    image:
      "https://customer-assets-wrfwihn1.emergentagent.net/job_spice-hub-156/artifacts/96a6fb2c373b3347_Gemini_Generated_Image_5khd9s5khd9s5khd.webp",
    specs: ["40–50 sticks", "ETO-free head", "OEM printing"],
    tone: "navy",
  },
  {
    slug: "wheat-aata",
    name: "Wheat Aata",
    subtitle: "Whole Wheat Flour",
    tagline: "Chakki-fresh · Stone-ground",
    description:
      "Chakki-fresh whole wheat flour milled from premium Sharbati wheat — high water absorption and consistent chapati quality.",
    image:
      "https://images.pexels.com/photos/326082/pexels-photo-326082.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1200",
    specs: ["Protein 11–13%", "Moisture < 14%", "Retail / bulk packs"],
    tone: "wheat",
  },
];

/** Signature clipped frames (each card gets a slightly different clip-path) */
const CLIPS = [
  "polygon(0 0, 100% 3%, 100% 100%, 0 97%)",
  "polygon(0 4%, 100% 0, 100% 96%, 0 100%)",
  "polygon(0 0, 100% 0, 100% 100%, 4% 96%)",
  "polygon(4% 0, 100% 4%, 96% 100%, 0 96%)",
  "polygon(0 3%, 100% 0, 97% 100%, 0 100%)",
  "polygon(3% 0, 100% 3%, 100% 97%, 0 100%)",
  "polygon(0 0, 97% 3%, 100% 100%, 4% 97%)",
  "polygon(2% 3%, 100% 0, 98% 100%, 0 97%)",
];

export const Products = () => {
  return (
    <section
      id="products"
      className="relative py-24 md:py-32 bg-white border-t border-[#E2E8F0] overflow-hidden"
      data-testid="products-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9 }}
          className="flex items-center gap-4 mb-6"
        >
          <span className="h-px w-16 bg-[#D4AF37]" />
          <span className="font-accent text-[10px] text-[#475569]">
            The Catalogue
          </span>
        </motion.div>

        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
            className="font-display font-bold text-[#0A192F] leading-[0.95] tracking-tight max-w-3xl"
            data-testid="products-heading"
            style={{ fontSize: "clamp(2.5rem, 5.5vw, 5rem)" }}
          >
            Eight categories.
            <br />
            <span className="italic font-normal text-[#D4AF37]">One standard</span> of quality.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="text-[#475569] max-w-sm text-base leading-relaxed"
          >
            From premium Indian turmeric to industrial coconut shell powder —
            every product is sourced, processed and shipped to your exact
            specification.
          </motion.p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-6 md:gap-8">
          {PRODUCTS.map((p, idx) => {
            const total = PRODUCTS.length;
            const spans =
              idx === 0
                ? "md:col-span-4 md:row-span-2"
                : total === 7 && idx === total - 1
                ? "md:col-span-4"
                : "md:col-span-2";
            return (
              <ProductCard
                key={p.slug}
                p={p}
                idx={idx}
                clip={CLIPS[idx]}
                spans={spans}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
};

const ProductCard = ({ p, idx, clip, spans }) => {
  const featured = idx === 0;
  return (
    <motion.article
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{
        duration: 1,
        delay: idx * 0.08,
        ease: [0.19, 1, 0.22, 1],
      }}
      className={`group relative flex flex-col ${spans}`}
      data-testid={`product-card-${p.slug}`}
    >
      <Link
        to={`/products/${p.slug}`}
        className="flex flex-col h-full"
        data-testid={`product-link-${p.slug}`}
      >
        {/* Clipped image frame */}
        <div
          className="relative overflow-hidden bg-[#0A192F]"
          style={{ clipPath: clip }}
        >
          <motion.img
            src={p.image}
            alt={p.name}
            className={`w-full object-cover ${
              featured ? "h-[420px] md:h-[540px]" : "h-64 md:h-72"
            }`}
            initial={{ scale: 1.05 }}
            whileHover={{ scale: 1.12 }}
            transition={{ duration: 1.4, ease: [0.19, 1, 0.22, 1] }}
          />
          {/* Spotlight overlay */}
          <div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
            style={{
              background:
                "radial-gradient(50% 50% at 50% 50%, rgba(212,175,55,0.18) 0%, transparent 70%)",
            }}
          />
          {/* Dark veil at bottom */}
          <div
            className="absolute inset-x-0 bottom-0 h-1/2 pointer-events-none"
            style={{
              background:
                "linear-gradient(to top, rgba(6,15,30,0.75) 0%, transparent 100%)",
            }}
          />

          {/* Index tag */}
          <div className="absolute top-5 left-5 flex items-center gap-2">
            <span className="bg-[#D4AF37] text-[#0A192F] px-2.5 py-1 font-accent text-[9px]">
              0{idx + 1}
            </span>
          </div>

          {/* Bottom title on image */}
          <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 text-white">
            <div className="font-accent text-[10px] text-[#D4AF37] mb-2">
              {p.tagline}
            </div>
            <h3
              className="font-display font-bold leading-none tracking-tight"
              style={{
                fontSize: featured
                  ? "clamp(2.5rem, 5vw, 4.5rem)"
                  : "clamp(1.5rem, 2.4vw, 2.25rem)",
              }}
            >
              {p.name}
              <span className="block text-white/70 font-normal italic" style={{ fontSize: "0.5em", marginTop: featured ? '0.35em' : '0.25em' }}>
                {p.subtitle}
              </span>
            </h3>
          </div>
        </div>

        {/* Card foot */}
        <div className="mt-6 flex flex-col flex-1">
          <p className="text-sm text-[#475569] leading-relaxed max-w-lg">
            {p.description}
          </p>
          <ul className="mt-5 flex flex-wrap gap-2">
            {p.specs.map((s) => (
              <li
                key={s}
                className="text-[11px] px-3 py-1 border border-[#E2E8F0] text-[#475569] tracking-wide"
              >
                {s}
              </li>
            ))}
          </ul>

          <div className="mt-6 pt-5 border-t border-[#E2E8F0] flex items-center justify-between">
            <span className="font-accent text-[10px] text-[#0A192F] group-hover:text-[#D4AF37] transition-colors">
              Explore Product
            </span>
            <ArrowUpRight
              size={20}
              className="text-[#0A192F] group-hover:text-[#D4AF37] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500"
            />
          </div>
        </div>
      </Link>
    </motion.article>
  );
};

export default Products;
